import { useState } from "react";
import { Check, Clock, Target } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ChecklistItem {
  task: string;
  description?: string;
  estimatedTime?: string;
  done: boolean;
}

interface Goal {
  title: string;
  currentMetric?: string;
  targetMetric?: string;
  priority?: string;
  checklistItems: ChecklistItem[];
}

interface QuickWin {
  task: string;
  impact?: string;
  done: boolean;
}

interface ActionPlan {
  summary: string;
  goals: Goal[];
  quickWins: QuickWin[];
  weeklyMilestones?: any[];
}

interface ActionPlanChecklistProps {
  plan: ActionPlan;
  planId: string;
  onProgressUpdate?: (progress: number) => void;
}

export const ActionPlanChecklist = ({ plan, planId, onProgressUpdate }: ActionPlanChecklistProps) => {
  const [localPlan, setLocalPlan] = useState(plan);

  const calculateProgress = (updatedPlan: ActionPlan) => {
    const allItems = [
      ...(updatedPlan.quickWins || []),
      ...(updatedPlan.goals || []).flatMap(g => g.checklistItems || []),
    ];
    const completedItems = allItems.filter(item => item.done).length;
    return allItems.length > 0 ? Math.round((completedItems / allItems.length) * 100) : 0;
  };

  const handleToggleQuickWin = async (index: number) => {
    const updatedQuickWins = [...localPlan.quickWins];
    updatedQuickWins[index] = { ...updatedQuickWins[index], done: !updatedQuickWins[index].done };

    const updatedPlan = { ...localPlan, quickWins: updatedQuickWins };
    setLocalPlan(updatedPlan);

    const progress = calculateProgress(updatedPlan);
    
    // Update database
    const { error } = await supabase
      .from('action_plans')
      .update({
        plan_content: JSON.stringify(updatedPlan),
        progress_percentage: progress,
      })
      .eq('id', planId);

    if (error) {
      toast.error("Failed to save progress");
      console.error(error);
    } else {
      toast.success(updatedQuickWins[index].done ? "Task completed! 🎉" : "Task marked incomplete");
      onProgressUpdate?.(progress);
    }
  };

  const handleToggleChecklistItem = async (goalIndex: number, itemIndex: number) => {
    const updatedGoals = [...localPlan.goals];
    updatedGoals[goalIndex].checklistItems[itemIndex] = {
      ...updatedGoals[goalIndex].checklistItems[itemIndex],
      done: !updatedGoals[goalIndex].checklistItems[itemIndex].done,
    };

    const updatedPlan = { ...localPlan, goals: updatedGoals };
    setLocalPlan(updatedPlan);

    const progress = calculateProgress(updatedPlan);
    
    // Update database
    const { error } = await supabase
      .from('action_plans')
      .update({
        plan_content: JSON.stringify(updatedPlan),
        progress_percentage: progress,
      })
      .eq('id', planId);

    if (error) {
      toast.error("Failed to save progress");
      console.error(error);
    } else {
      toast.success(updatedGoals[goalIndex].checklistItems[itemIndex].done ? "Task completed! 🎉" : "Task marked incomplete");
      onProgressUpdate?.(progress);
    }
  };

  const progress = calculateProgress(localPlan);
  const totalItems = [
    ...(localPlan.quickWins || []),
    ...(localPlan.goals || []).flatMap(g => g.checklistItems || []),
  ].length;
  const completedItems = [
    ...(localPlan.quickWins || []),
    ...(localPlan.goals || []).flatMap(g => g.checklistItems || []),
  ].filter(item => item.done).length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Action Plan Progress
          </CardTitle>
          <CardDescription>{localPlan.summary}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>{completedItems} of {totalItems} tasks completed</span>
              <span className="font-semibold">{progress}%</span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>
        </CardContent>
      </Card>

      {localPlan.quickWins && localPlan.quickWins.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              ⚡ Quick Wins
            </CardTitle>
            <CardDescription>Start here for immediate impact</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {localPlan.quickWins.map((win, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <Checkbox
                  checked={win.done}
                  onCheckedChange={() => handleToggleQuickWin(index)}
                  className="mt-1"
                />
                <div className="flex-1 space-y-1">
                  <p className={`font-medium ${win.done ? 'line-through text-muted-foreground' : ''}`}>
                    {win.task}
                  </p>
                  {win.impact && (
                    <p className="text-sm text-muted-foreground">
                      Expected impact: {win.impact}
                    </p>
                  )}
                </div>
                {win.done && <Check className="h-5 w-5 text-green-500" />}
              </motion.div>
            ))}
          </CardContent>
        </Card>
      )}

      {localPlan.goals && localPlan.goals.map((goal, goalIndex) => (
        <Card key={goalIndex}>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2">
                  {goal.title}
                  {goal.priority && (
                    <Badge variant={goal.priority === 'high' ? 'destructive' : 'default'}>
                      {goal.priority}
                    </Badge>
                  )}
                </CardTitle>
                {(goal.currentMetric || goal.targetMetric) && (
                  <CardDescription>
                    {goal.currentMetric && `Current: ${goal.currentMetric}`}
                    {goal.currentMetric && goal.targetMetric && ' → '}
                    {goal.targetMetric && `Target: ${goal.targetMetric}`}
                  </CardDescription>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {goal.checklistItems && goal.checklistItems.map((item, itemIndex) => (
              <motion.div
                key={itemIndex}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: itemIndex * 0.1 }}
                className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <Checkbox
                  checked={item.done}
                  onCheckedChange={() => handleToggleChecklistItem(goalIndex, itemIndex)}
                  className="mt-1"
                />
                <div className="flex-1 space-y-1">
                  <p className={`font-medium ${item.done ? 'line-through text-muted-foreground' : ''}`}>
                    {item.task}
                  </p>
                  {item.description && (
                    <p className="text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  )}
                  {item.estimatedTime && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {item.estimatedTime}
                    </div>
                  )}
                </div>
                {item.done && <Check className="h-5 w-5 text-green-500" />}
              </motion.div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};